import { Interweave } from 'interweave';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getCurrentLangPretty } from '../../../../../../helpers/url/urlHelpers';
import useCountries from '../../../../../../hooks/useCountries';
import usePostCodes from '../../../../../../hooks/usePostCodes';
import IUserProfileReducer from '../../../../../../interfaces/reducers/userProfileReducer';
import IPageViewModel from '../../../../../../interfaces/umbracoNodes/pageViewModel';
import { IStore } from '../../../../../../reducers';
import FormikCheckbox from '../../../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../../../base/forms/formikSelect';
import { StyledFormikFastField } from '../../../../../base/forms/input';
import { Label } from '../../../../../base/forms/label';
import PhoneCountryInput from '../../../../../base/forms/phoneCountryInput';
import { Column } from '../../../../../base/grid';
import { A } from '../../../../../base/typography/anchors';
import { getBusinessAccountRegisterShipmentUrl, isCompanySender } from '../../helpers';
import { IStepInfoFormInfo } from '../../stepInfo.schema';
import * as StyledStepInfoForm from '../stepInfoForm.styles';

interface Props {
	page: IPageViewModel;
	values: IStepInfoFormInfo;
	setFieldTouched: any;
	setFieldValue: any;
	codIncluded: boolean;
}

const SenderFields: FC<Props> = ({ page, values, setFieldTouched, setFieldValue, codIncluded }) => {
	const { t } = useTranslation('registerShipment');
	const ssnRequired = codIncluded;
	if (ssnRequired && values.senderHasForeignSsn) {
		// if user has marked the checkbox when it is not allowed this will unmark it
		setFieldValue('senderHasForeignSsn', false);
	}
	const { senderHasForeignSsn } = values;
	const { postCodes } = usePostCodes();
	const lang = getCurrentLangPretty();
	const { countries } = useCountries(lang, true);
	const senderIsCompany = isCompanySender(values);
	// const [emailWarning, setEmailWarning] = useState(null);

	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile } = userProfileReducer;

	// const handleEmailBlur = async (field: any, e: any) => {
	// 	field.onBlur(e);
	// 	await handleEmailValidation(e.currentTarget.value);
	// };

	// const handleEmailChange = async (field: any, e: any) => {
	// 	field.onChange(e);
	// 	await handleEmailValidation(e.currentTarget.value);
	// };

	// const handleEmailValidation = async (value: any) => {
	// 	const warningMessage = await validateEmail(value, t);
	// 	setEmailWarning(warningMessage);
	// };

	return (
		<>
			<StyledStepInfoForm.MarginDiv>
				<h2>{t('shared.sales-push')}</h2>
			</StyledStepInfoForm.MarginDiv>
			{/*<StyledStepInfoForm.RedP>{t('shared.wwiii')}</StyledStepInfoForm.RedP>*/}
			<StyledStepInfoForm.Pdiv>
				<Interweave content={page?.Values?.InfoText} noWrap />
			</StyledStepInfoForm.Pdiv>

			{/* <h2>{t('shared.sales-push')}</h2>
			<P>
				<Trans
					t={t}
					i18nKey={'shared.postbox'}
					components={[<A href={getCurrentLangPretty() === 'is' ? '/minar-sidur' : '/en/my-account'} />]}
				/>
			</P>
			<P>
				<Trans
					t={t}
					i18nKey={'shared.postoffice'}
					components={[<A href={getCurrentLangPretty() === 'is' ? '/minar-sidur' : '/en/my-account'} />]}
				/>
			</P>
			<P>{t('shared.thenWhat')}</P>
			<Ul marginBottom={'3rem'}>
				<Li>{t('shared.li1')}</Li>
				<Li>{t('shared.li2')}</Li>
				<Li>{t('shared.li3')}</Li>
			</Ul> */}
			<Label>{t('shared.sender')}</Label>
			<StyledStepInfoForm.FieldColumns marginBottom="1.5rem">
				<Column>
					<FormikFieldWrapper name="senderHasForeignSsn" languageComponent="registerShipment">
						<FormikCheckbox
							name="senderHasForeignSsn"
							id="senderHasForeignSsn"
							label={t('step-info.sender-foreign')}
							disabled={ssnRequired}
							setFieldValue={setFieldValue}
						/>
					</FormikFieldWrapper>
				</Column>
			</StyledStepInfoForm.FieldColumns>
			<StyledStepInfoForm.FieldColumns>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderName"
						label={`${t('step-info.name')}*`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="text"
							id="senderName"
							name="senderName"
							placeholder={t('step-info.name')}
							autoComplete="name"
							disabled={userProfile !== null}
						/>
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderSsn"
						label={`${t('step-info.ssn')}${ssnRequired || !senderHasForeignSsn ? '*' : ''}`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="text"
							id="senderSsn"
							name="senderSsn"
							placeholder={t('step-info.ssn')}
							disabled={userProfile !== null}
						/>
					</FormikFieldWrapper>
					{senderIsCompany && (
						<StyledStepInfoForm.SenderBusinessAccountWarning>
							<Trans
								i18nKey={'step-info.companySenderBusinessAccountInfo'}
								ns="registerShipment"
								components={[<A href={getBusinessAccountRegisterShipmentUrl(lang)} target="_blank" />]}
							/>
						</StyledStepInfoForm.SenderBusinessAccountWarning>
					)}
				</Column>
			</StyledStepInfoForm.FieldColumns>
			<StyledStepInfoForm.FieldColumns>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderAddress"
						label={`${t('step-info.address')}*`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="text"
							id="senderAddress"
							name="senderAddress"
							placeholder={t('step-info.address')}
							autoComplete="address"
						/>
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderPostCode"
						label={`${t('step-info.postcode')}*`}
						smallLabel
						languageComponent="registerShipment">
						<FormikSelect
							id="senderPostCode"
							name="senderPostCode"
							value={values.senderPostCode}
							options={postCodes}
							placeholder={t('step-info.postcode')}
							onBlur={setFieldTouched}
							onChange={setFieldValue}
							autocomplete="postal-code"
						/>
					</FormikFieldWrapper>
				</Column>
			</StyledStepInfoForm.FieldColumns>
			<StyledStepInfoForm.FieldColumns>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderEmail"
						label={`${t('step-info.email')}*`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="senderEmail"
							id="senderEmail"
							name="senderEmail"
							placeholder={t('step-info.email')}
							autoComplete="email"
						/>
						{/* <FastField id="senderEmail" name="senderEmail">
							{({ field }) => (
								<div>
									<Input
										type="text"
										id={field.id}
										name={field.name}
										value={field.value}
										onBlur={(e: any) => handleEmailBlur(field, e)}
										onChange={(e: any) => handleEmailChange(field, e)}
										placeholder={t('step-info.email')}
										autoComplete="email"
									/>
								</div>
							)}
						</FastField> */}
						{/* {emailWarning && (
							<StyledStepInfoForm.WarningMessage>{t(emailWarning)}</StyledStepInfoForm.WarningMessage>
						)} */}
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="senderPhone"
						label={`${t('step-info.genericPhone')}*`}
						smallLabel
						languageComponent="registerShipment">
						<PhoneCountryInput
							id="senderPhone"
							name="senderPhone"
							value={values.senderPhone}
							countryCodeName="senderPhoneCountryCode"
							countryCodeValue={values.senderPhoneCountryCode}
							countries={countries}
							onBlur={setFieldTouched}
							setFieldValue={setFieldValue}
							defaultCountryCode="IS"
							placeholder={t('step-info.genericPhone')}
							searchPlaceholder={t('step-info.phone-country-search')}
							autoComplete="tel"
						/>
					</FormikFieldWrapper>
				</Column>
			</StyledStepInfoForm.FieldColumns>
		</>
	);
};

export default SenderFields;
